import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

type AkismetEntry = {
  site: string;
  api_calls: number;
  spam: number;
  ham: number;
  missed_spam: number;
  false_positives: number;
  is_revoked: boolean;
};

type AkismetMonthData = Record<string, AkismetEntry[]> & {
  limit: number;
  offset: number;
  total: number;
};

interface AkismetGraphProps {
  data: AkismetMonthData[];
}

export const AkismetGraph: React.FC<AkismetGraphProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <p className="text-red-600">
        Akismet data was not available. Please try again or contact support.
      </p>
    );
  }

  // Flatten and aggregate data by month
  const chartData: {
    month: string;
    spam: number;
    ham: number;
    missed_spam: number;
  }[] = [];

  data.forEach((monthData) => {
    Object.entries(monthData)
      .filter(([key]) => !["limit", "offset", "total"].includes(key))
      .forEach(([month, entries]) => {
        const akismetEntries = entries as AkismetEntry[]; // <-- cast here
        const aggregated = akismetEntries.reduce(
          (acc, entry) => ({
            spam: acc.spam + Number(entry.spam),
            ham: acc.ham + Number(entry.ham),
            missed_spam: acc.missed_spam + Number(entry.missed_spam),
          }),
          { spam: 0, ham: 0, missed_spam: 0 }
        );
        chartData.push({
          month,
          spam: aggregated.spam,
          ham: aggregated.ham,
          missed_spam: aggregated.missed_spam,
        });
      });
  });

  chartData.sort((a, b) => a.month.localeCompare(b.month));

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-6">
      <div className="col-span-full">
        <h3 className="text-lg font-semibold mb-2">Akismet</h3>
        <p className="text-gray-500 mb-4">
          Understand how we reduce spam on your site. Each line represents a
          different metric:
          <br />
          <span className="inline-flex items-center mr-4">
            <span className="w-3 h-3 bg-red-600 rounded-full mr-1"></span>
            <strong>Spam</strong>: Messages correctly identified as spam by
            Akismet.
          </span>
          <br />
          <span className="inline-flex items-center mr-4">
            <span className="w-3 h-3 bg-green-600 rounded-full mr-1"></span>
            <strong>Ham</strong>: Messages correctly identified as legitimate
            (non-spam).
          </span>
          <br />
          <span className="inline-flex items-center">
            <span className="w-3 h-3 bg-orange-600 rounded-full mr-1"></span>
            <strong>Missed Spam</strong>: Spam messages that Akismet failed to
            detect.
          </span>
        </p>

        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                stroke="#9ca3af"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#9ca3af"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(value: number, name: string) => `${value}`}
                labelFormatter={(label: string) => `Month: ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="spam"
                stroke="#e53e3e"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="ham"
                stroke="#38a169"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="missed_spam"
                stroke="#dd6b20"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
